import { Button, Spinner } from "@chakra-ui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "axios";
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  lazy,
  Suspense,
} from "react";
import {
  BRANDS_LIST,
  getAxiosConfig,
  getUpdatedImageUrlWithSize,
} from "../../../../../utils/helpers";
import classNames from "classnames";
import ImgOptimized from "../../../../../components/ImgOptimized";
import Img from "../../../../../components/Img";
import FullPageImageSlider from "../../../../../components/FullPageImageSlider";
import { motion, AnimatePresence } from "framer-motion";

const ScannerMotion = ({ loading }) => {
  return (
    <AnimatePresence>
      {loading && (
        <motion.div
          className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Scanning line */}
          <motion.div
            className="absolute left-[5%] w-[90%] h-[2px] rounded-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, #14b8a6, #5eead4, #14b8a6, transparent)",
              boxShadow:
                "0 0 15px 3px rgba(20,184,166,0.6), 0 0 40px rgba(20,184,166,0.15)",
            }}
            animate={{ top: ["5%", "95%", "5%"] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Corner brackets */}
          {[
            {
              pos: "top-2 left-2",
              border: "border-t-2 border-l-2 rounded-tl-md",
            },
            {
              pos: "top-2 right-2",
              border: "border-t-2 border-r-2 rounded-tr-md",
            },
            {
              pos: "bottom-2 left-2",
              border: "border-b-2 border-l-2 rounded-bl-md",
            },
            {
              pos: "bottom-2 right-2",
              border: "border-b-2 border-r-2 rounded-br-md",
            },
          ].map(({ pos, border }, i) => (
            <motion.div
              key={i}
              className={`absolute w-6 h-6 border-teal-400 ${pos} ${border}`}
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.3,
              }}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// export default React.memo(ScannerMotion);

// ScannerLottie.displayName = "ScannerLottie";

const BRANDS = [
  "Monarch",
  "Philips Lighting",
  "Asian Paints",
  "Herman Miller",
  "Merino",
  "Nilkamal",
  "Steelcase",
  "Armstrong Flooring",
  "Viak",
  "Haworth India",
  "Havells Lighting",
  "Durian",
  "Pergo (LVP)",
  "Ask Modular",
  "Greenlam",
  "Ankur Lighting",
  "Wipro Lighting",
  "Orient Electric",
  "Featherlite",
  "Ergo Space",
  "Responsive Industries",
];

// const spaceMapping = {
//   "Board Room": "Boardroom",
//   "Breakout Area": "Breakout Space",
//   "CEO Cabin": "Private Office",
//   Cafeteria: "Work Lounge",
//   "Manager Cabin": "Private Office",
//   "Meeting Room": "Small Meeting Room",
//   "Open Office": "Open Office",
//   Pantry: "Pantry Area",
//   Reception: "Reception Waiting Area",
//   Washroom: "Rest Room",
// };

const Step_04_material = ({
  possibleOptions,
  filters,
  setFilters,
  materials,
  handleSaveMaterial,
  isPublicView,
  navigatePublicView,
  projectId,
  SetDesignDataForExistingMaterials,
  setNewFilter,
  newFilter,
  state,
  selectedProjectImage,
  selectedProjectImageUrl,
  setSelectedProjectImage,
  areas,
  setAreas,
  aiRecommendations,
  setAiRecommendations,
  setDesignData,
  stateProjectImage,
  aiImageLoading,
  setAiImageLoading,
}) => {
  const open = {
    zone: filters?.zone?.[0] || null,
    section: filters?.section?.[0] || null,
  };
  const [isZone, setIsZone] = useState(state?.isZone ?? true);
  const loading = aiImageLoading;
  const [areasFilters, setAreasFilters] = useState(state?.areasFilters || []);
  const [lottieVisible, setLottieVisiblity] = useState(false);
  const [selectedImageIdx, setSelectedImageIdx] = useState(null);
  const [showAnalysisFallbackNote, setShowAnalysisFallbackNote] =
    useState(false);

  // ✅ Use ref for the lottie visibility timer to properly clean it up
  const lottieTimerRef = useRef(null);

  console.log({ materials });
  console.log("Filters changed", filters);

  async function fetchZonesByImage() {
    try {
      if (!selectedProjectImage) {
        setAreas([]);
        setShowAnalysisFallbackNote(false);
        return;
      }
      setAiImageLoading(true);

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/image-filtering`,
        {
          imageUrl: getUpdatedImageUrlWithSize(
            `${process.env.REACT_APP_BACK_URL}/api/minio/${selectedProjectImage}`,
            [3, 3, 3],
          ),
        },
        getAxiosConfig(),
      );
      let area = [];
      (data.areas || []).forEach((a) => {
        area.push(a);
      });

      if (area.length == 0) {
        setShowAnalysisFallbackNote(true);
        setAreas(possibleOptions?.zone || []);

        setFilters(
          possibleOptions?.zone?.[0] || null,
          possibleOptions?.section?.[0] || null,
        );
      } else {
        setShowAnalysisFallbackNote(false);
        setAreas(area);
        const formattedAreas = (data?.areasFilter || []).map((item) => {
          const all = item.all || [];

          const hasPaints = all.includes("Paints");

          return {
            ...item,
            all: hasPaints
              ? ["Paints", ...all.filter((val) => val !== "Paints")]
              : all,
          };
        });

        setAreasFilters(formattedAreas);

        setFilters(area?.[0], formattedAreas?.[0]?.all?.[0] || null);
        //  setOpen ({
        //     zone: area?.[0] || null,
        //     section: formattedAreas?.[0]?.all?.[0] || null,
        //   });
      }
      setAiRecommendations((data?.materials || []).map((v) => v._id));
    } catch (error) {
      console.error("Failed to fetch zones by image", error);
      setShowAnalysisFallbackNote(true);
      setAreas(possibleOptions?.zone || []);
      setFilters(
        possibleOptions?.zone?.[0] || null,
        possibleOptions?.section?.[0] || null,
      );
    } finally {
      setAiImageLoading(false);
    }
  }

  const fetchProjectImgs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/images/${projectId}`,
      );
      console.log({ res: response.data });
      const materialsData = (response.data?.projectImageMaterials || []).find(
        (v) => v.image == selectedProjectImage,
      )?.materials;
      let selectedMaterial = {};
      materialsData?.forEach((m) => {
        if (selectedMaterial[m.zone]) {
          selectedMaterial[m.zone].push({
            materialId: m.materialId?._id || m?.materialId,
            material: m?.materialId,
            section: m.section,
          });
        } else {
          selectedMaterial[m.zone] = [
            {
              materialId: m.materialId?._id || m?.materialId,
              material: m?.materialId,
              section: m.section,
            },
          ];
        }
      });
      SetDesignDataForExistingMaterials(
        projectId,
        response.data?.project?.title || "test-name",
        selectedMaterial,
      );
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Failed to fetch project data", error);
    }
  };

  // useEffect(() => {
  //   console.log("OPEN VALUEVALUE", open);
  //   setFilters(open.zone, open.section);
  // }, [open]);

  useEffect(() => {
    if (stateProjectImage != selectedProjectImage) {
      fetchProjectImgs();
    }
  }, [
    selectedProjectImage,
    JSON.stringify(stateProjectImage),
    JSON.stringify(state?.areas),
  ]);

  useEffect(() => {
    console.log(
      "SELECTEDPROJECTIMAGE",
      selectedProjectImage,
      selectedProjectImageUrl,
      possibleOptions,
    );
    if (
      selectedProjectImage &&
      state?.selectedProjectImage != selectedProjectImage
    ) {
      setAreas([]);
      setFilters(null, null, true);
      setDesignData((p) => ({ ...p, selectedMaterial: {} }));
      fetchZonesByImage();
    }
  }, [
    selectedProjectImage,
    JSON.stringify(stateProjectImage),
    JSON.stringify(state?.areas),
  ]);

  return (
    <div className="h-full flex flex-col justify-between ">
      <div className="mb-4">
        <div className="relative">
          <ImgOptimized
            isLazy={false}
            onClick={() => {
              setSelectedImageIdx(0);
            }}
            className={`rounded-xl w-full h-full max-h-48 object-contain cursor-pointer`}
            src={getUpdatedImageUrlWithSize(
              `${process.env.REACT_APP_BACK_URL}/api/minio/${selectedProjectImage}`,
              [1, 1, 1],
            )}
            alt="Section main"
          />
          <div
            className="absolute bottom-2 right-2 w-fit h-fit max-h-6 max-w-6 flex items-center justify-center bg-black bg-opacity-50 rounded-full p-1 text-white hover:bg-opacity-80 transition-all cursor-pointer"
            onClick={() => setSelectedImageIdx(0)}
          >
            <Icon icon="mdi:fullscreen" width={18} />
          </div>
          {/* ✅ Lottie now fully unmounts when not needed */}
          <ScannerMotion loading={loading} />
        </div>
      </div>

      <div className="grow min-h-0 flex flex-col gap-4 my-2 overflow-y-auto">
        {loading ? (
          <div className="h-40 flex flex-col gap-4 items-center justify-center relative mt-4">
            {/* Background glow effect */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 bg-teal-400/20 rounded-full blur-2xl animate-pulse" />
            </div>

            {/* Spinning rings */}
            <div className="relative flex items-center justify-center">
              {/* Outer ring */}
              <div className="absolute w-20 h-20 border-4 border-teal-200/30 border-t-teal-500 rounded-full animate-spin" />
              {/* Inner ring - reverse spin */}
              <div
                className="absolute w-14 h-14 border-4 border-purple-200/30 border-b-purple-500 rounded-full animate-spin direction-reverse"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              />
              {/* Center dot */}
              <div className="w-3 h-3 bg-teal-500 rounded-full animate-pulse" />
            </div>

            {/* Animated text */}
            <div className="mt-8 flex flex-col items-center gap-2">
              <p className="text-lg font-semibold bg-gradient-to-r from-teal-400 via-cyan-500 to-purple-500 bg-clip-text text-transparent animate-pulse">
                Analyzing Image
              </p>
              {/* Animated dots */}
              <div className="flex gap-1.5">
                <span
                  className="w-2 h-2 bg-teal-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                />
                <span
                  className="w-2 h-2 bg-teal-400 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                />
                <span
                  className="w-2 h-2 bg-teal-300 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {showAnalysisFallbackNote && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                There is a problem analyzing this image. You can manually select
                related areas below.
              </div>
            )}
            {possibleOptions?.zone?.filter((z) => areas.includes(z)).length >
            0 ? (
              <>
                {possibleOptions?.zone
                  ?.filter((z) => areas.includes(z))
                  .map((z) => (
                    <div key={z} className={`bg-white border rounded-lg`}>
                      <div
                        onClick={() => {
                          //  setOpen ({
                          //     zone: open?.zone == z ? null : z,
                          //     section: null,
                          //   });
                          setFilters(open?.zone == z ? null : z, null);
                          setNewFilter((p) => {
                            return { ...p, types: [], search: "" };
                          });
                        }}
                        className={`p-2 text-md capitalize font-semibold flex justify-between items-center cursor-pointer ${
                          open?.zone == z
                            ? "bg-teal-200 border border-teal-500 rounded-lg"
                            : ""
                        }`}
                      >
                        <div>
                          {z}
                          <div className="text-xs text-gray-400">
                            {[...new Set(materials?.[z]?.map((v) => v.section))]
                              .length || 0}
                            /
                            {
                              (
                                areasFilters.find((a) => a?._id == z)?.all ||
                                possibleOptions?.section
                              )?.length
                            }{" "}
                            sections selected
                          </div>
                        </div>
                        <Icon
                          icon={
                            open?.zone == z
                              ? "mdi:chevron-up"
                              : "mdi:chevron-down"
                          }
                        />
                      </div>

                      {open?.zone == z && (
                        <div className="mt-2 space-y-2">
                          {(
                            areasFilters.find((a) => a?._id == z)?.all ||
                            possibleOptions?.section?.filter((section) => {
                              if (z == "Washroom") {
                                return section == "Bathroom Fittings";
                              } else {
                                return section != "Bathroom Fittings";
                              }
                            })
                          ).map((s) => (
                            <div
                              key={s}
                              onClick={() => {
                                // setOpen((p) => ({ ...p, section: s }));
                                setFilters(open?.zone, s);

                                setNewFilter((p) => {
                                  return { ...p, types: [s], search: "" };
                                });
                              }}
                              className={`p-2 text-sm pl-6 cursor-pointer flex justify-between items-center ${
                                open?.section == s
                                  ? "bg-green-200 text-black border border-green-700 rounded-lg hover:bg-green-300"
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              {s}
                              {materials?.[open?.zone]?.find(
                                (m) => m.section == s,
                              ) && (
                                <Icon
                                  icon="mdi:check"
                                  color="green"
                                  width={20}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </>
            ) : (
              <div className="h-28 flex flex-col gap-2 items-center justify-center ">
                No Areas Found
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 mb-0">
        {isPublicView && (
          <Button
            onClick={() =>
              navigatePublicView({
                open,
                isZone,
                selectedProjectImage,
                projectId,
                areas,
                areasFilters,
                aiRecommendations,
              })
            }
            colorScheme="teal"
            w={"100%"}
            variant={"outline"}
          >
            Public View
          </Button>
        )}
        <Button onClick={handleSaveMaterial} colorScheme="teal" w={"100%"}>
          Save
        </Button>
      </div>
      {selectedImageIdx !== null && (
        <FullPageImageSlider
          images={[selectedProjectImage]}
          isOpen={selectedImageIdx !== null}
          onClose={() => setSelectedImageIdx(null)}
          initialIndex={selectedImageIdx}
        />
      )}
    </div>
  );
};

export default Step_04_material;
